import { useAnimationSettings } from '@/context/AnimationContext'; // Import the context hook

// Define keyframes - NOTE: These should ideally be in a global CSS file
// or handled by a CSS-in-JS library, not directly in JS like this.
// If you move them to index.css, you can remove this style injection.
const animationStyles = `
  @keyframes gradientShift {
    0% { background-position: 0% 50%; filter: hue-rotate(0deg); }
    50% { background-position: 100% 50%; filter: hue-rotate(45deg); }
    100% { background-position: 0% 50%; filter: hue-rotate(0deg); }
  }

  @keyframes bubbleFloat1 {
    0% { transform: translate(0, 0); opacity: 1; filter: hue-rotate(0deg); }
    33% { transform: translate(-30px, 37.5px); opacity: 0.9; filter: hue-rotate(30deg); }
    66% { transform: translate(-60px, 75px); opacity: 0.8; filter: hue-rotate(60deg); }
    100% { transform: translate(0, 0); opacity: 1; filter: hue-rotate(0deg); }
  }

  @keyframes bubbleFloat2 {
    0% { transform: translate(0, 0); opacity: 1; filter: hue-rotate(0deg); }
    33% { transform: translate(40.5px, -25.5px); opacity: 0.9; filter: hue-rotate(-40deg); }
    66% { transform: translate(82.5px, -52.5px); opacity: 0.8; filter: hue-rotate(-80deg); }
    100% { transform: translate(0, 0); opacity: 1; filter: hue-rotate(0deg); }
  }

  @keyframes bubbleFloat3 {
    0% { transform: translate(0, 0); opacity: 1; filter: hue-rotate(0deg); }
    33% { transform: translate(-18px, -30px); opacity: 0.9; filter: hue-rotate(50deg); }
    66% { transform: translate(-37.5px, -60px); opacity: 0.85; filter: hue-rotate(100deg); }
    100% { transform: translate(0, 0); opacity: 1; filter: hue-rotate(0deg); }
  }

  @keyframes bubbleFloat4 {
    0% { transform: translate(0, 0); opacity: 1; filter: hue-rotate(0deg); }
    33% { transform: translate(33px, 22.5px); opacity: 0.9; filter: hue-rotate(-25deg); }
    66% { transform: translate(67.5px, 45px); opacity: 0.85; filter: hue-rotate(-50deg); }
    100% { transform: translate(0, 0); opacity: 1; filter: hue-rotate(0deg); }
  }
  
  @keyframes bubbleFloat5 {
    0% { transform: translate(0, 0); opacity: 1; filter: hue-rotate(0deg); }
    33% { transform: translate(-22.5px, -37.5px); opacity: 0.95; filter: hue-rotate(70deg); }
    66% { transform: translate(-45px, -75px); opacity: 0.9; filter: hue-rotate(140deg); }
    100% { transform: translate(0, 0); opacity: 1; filter: hue-rotate(0deg); }
  }
  
  @keyframes bubbleFloat6 {
    0% { transform: translate(0, 0); opacity: 1; filter: opacity-rotate(0deg); }
    33% { transform: translate(45px, 30px); opacity: 0.9; filter: hue-rotate(-60deg); }
    66% { transform: translate(90px, 60px); opacity: 0.85; filter: hue-rotate(-120deg); }
    100% { transform: translate(0, 0); opacity: 1; filter: hue-rotate(0deg); }
  }
`;

// Base styles for all bubbles
const accentBaseStyle: React.CSSProperties = {
  position: 'absolute',
  borderRadius: '50%',
  zIndex: 1,
  pointerEvents: 'none',
  aspectRatio: '1',
};

// Function to generate gradient background for bubbles
const createBubbleGradient = (color1Var: string, color2Var: string, opacity: number): string => {
  return `radial-gradient(circle, oklch(from var(${color1Var}) calc(l) calc(c) calc(h) / ${opacity}) 0%, oklch(from var(${color2Var}) calc(l) calc(c) calc(h) / 0) 90%)`;
};

// Function to generate animation string
const createAnimation = (animationsEnabled: boolean, animationName: string, duration: number): string => {
  return animationsEnabled ? `${animationName} ${duration}s ease-in-out infinite` : 'none';
};

// Function to create bubble styles
interface BubbleProps {
  position: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
  };
  size: string;
  color1Var: string;
  color2Var: string;
  opacity: number;
  animationName: string;
  duration: number;
  transitionTime?: number; // Add transition time property
}

const createBubbleStyle = (
  props: BubbleProps,
  animationsEnabled: boolean
): React.CSSProperties => {
  return {
    ...accentBaseStyle,
    ...props.position,
    width: props.size,
    height: props.size,
    background: createBubbleGradient(props.color1Var, props.color2Var, props.opacity),
    animation: createAnimation(animationsEnabled, props.animationName, props.duration),
    transition: `background ${props.transitionTime || 0.5}s ease`, // Custom transition time with fallback
  };
};

const SodaTrackBackground: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { animationsEnabled } = useAnimationSettings(); // Get state from context

  // Background container styles - Positioned behind content
  const backgroundStyle: React.CSSProperties = {
    position: 'fixed', // Use fixed to cover viewport regardless of scroll
    top: 0,
    left: 0,
    width: '100vw',    // Use vw for viewport width
    height: '100vh',   // Use vh for viewport height
    zIndex: -1,        // Place behind all other content
    overflow: 'hidden', // Keep bubbles contained
    // Use theme variables for a colorful gradient background
    background: 'linear-gradient(135deg, oklch(from var(--chart-1) calc(l) calc(c) calc(h) / 0.8), oklch(from var(--chart-2) calc(l) calc(c) calc(h) / 0.8) 100%)',
    // Apply a gentle hue rotation animation to the main background as well
    // Conditionally apply animation
    animation: animationsEnabled ? 'gradientShift 20s ease infinite alternate' : 'none',
    transition: 'background 0.7s ease', // Optional: Add transition for theme changes
  };

  // Define bubble configurations
  const bubbles = [
    {
      position: { top: '-40%', right: '5%' },
      size: '1000px',
      color1Var: '--chart-2',
      color2Var: '--chart-5',
      opacity: 0.65,
      animationName: 'bubbleFloat1',
      duration: 15,
      transitionTime: 0.8, // Custom transition time
    },
    {
      position: { bottom: '-25%', left: '10%' },
      size: '700px',
      color1Var: '--chart-3',
      color2Var: '--chart-4',
      opacity: 0.2,
      animationName: 'bubbleFloat2',
      duration: 18,
      transitionTime: 1.2, // Custom transition time
    },
    {
      position: { top: '-18%', left: '-15%' },
      size: '1000px',
      color1Var: '--chart-3',
      color2Var: '--chart-5',
      opacity: 0.3,
      animationName: 'bubbleFloat3',
      duration: 12,
      transitionTime: 0.6, // Custom transition time
    },
    {
      position: { top: '60%', right: '15%' },
      size: '560px',
      color1Var: '--chart-4',
      color2Var: '--chart-5',
      opacity: 0.25,
      animationName: 'bubbleFloat4',
      duration: 14,
      transitionTime: 1.0, // Custom transition time
    },
  ];

  return (
    // Use a React Fragment to return multiple elements
    <>
      {/* Background elements container */}
      <div style={backgroundStyle}>
         {/* Inject keyframe styles - consider moving to index.css */}
        <style>{animationStyles}</style>
        {/* Render bubbles from the array configuration */}
        {bubbles.map((bubble, index) => (
          <div 
            key={`bubble-${index}`} 
            style={createBubbleStyle(bubble, animationsEnabled)}
          ></div>
        ))}
      </div>
      {/* Render children directly, as siblings to the background container */}
      {children}
    </>
  );
};

export default SodaTrackBackground; 